import {noop, partition} from '@benchling/js-utils';
import produce from 'immer';
import {
  assertCompositeType,
  FieldNode,
  getNamedType,
  GraphQLFieldMap,
  GraphQLObjectType,
  InlineFragmentNode,
  isAbstractType,
  isCompositeType,
  isInterfaceType,
  isObjectType,
  Kind,
  SelectionSetNode,
} from 'query-api-graphql';
import React from 'react';

import {assert} from 'Asserts';

import countSelectedFields from './countSelectedFields';
import FieldRow from './FieldRow';
import {useGraphQLFieldSelectorContext} from './GraphQLFieldSelectorContext';
import {GraphQLField} from './GraphQLFieldSelectorTypes';
import Row from './Row';

function getSortedTypes(types: Array<GraphQLObjectType>): Array<GraphQLObjectType> {
  return types.slice().sort((a, b) => a.name.localeCompare(b.name));
}

function getNumSelectedDescription(isExpanded: boolean, numSelected: number): string | undefined {
  return !isExpanded && numSelected > 0 ? `${numSelected} selected` : undefined;
}

export const EMPTY_SELECTION_SET: SelectionSetNode = {
  selections: [],
  kind: Kind.SELECTION_SET,
};

interface ObjectFieldProps {
  fields: Array<GraphQLField>;
  indentationLevel: number;
  logicalDepth: number;
  parentStateId: string;
  selections: ReadonlyArray<FieldNode>;
  onChangeSelections: (newSelections: ReadonlyArray<FieldNode>) => void;
}

function ObjectFields({
  fields,
  indentationLevel,
  logicalDepth,
  parentStateId,
  selections,
  onChangeSelections,
}: ObjectFieldProps): JSX.Element {
  const {getSortedFilteredFields, renderCheckbox} = useGraphQLFieldSelectorContext();

  return (
    <>
      {getSortedFilteredFields(fields).map((field) => {
        const selection = selections.find((selection) => selection.name.value === field.name);
        const isSelected = selection != null;

        const sharedProps = {
          field,
          indentationLevel,
          key: field.name,
        };

        return isCompositeType(getNamedType(field.type)) ? (
          <CompositeField
            {...sharedProps}
            logicalDepth={logicalDepth}
            fieldStateId={`${parentStateId}-${field.name}`}
            selectionSet={selection?.selectionSet ?? EMPTY_SELECTION_SET}
            onChangeSelectionSet={(newSelectionSet) => {
              let newSelections: ReadonlyArray<FieldNode>;
              if (newSelectionSet.selections.length === 0) {
                // If there are no selections, do not include the composite field in the new selections
                // For example:
                // before: {creator {name}}
                // after: {}
                newSelections = selections.filter((selection) => selection.name.value !== field.name);
              } else if (isSelected) {
                // If the composite field is already in the selections, replace its old selections with
                // the new ones
                // For example:
                // before: {creator {name}}
                // after: {creator {name email}}
                newSelections = selections.map((selection) =>
                  selection.kind === Kind.FIELD && selection.name.value === field.name
                    ? {
                        kind: Kind.FIELD,
                        name: {kind: Kind.NAME, value: field.name},
                        selectionSet: newSelectionSet,
                      }
                    : selection
                );
              } else {
                // Otherwise, add the new field to the selections.
                // For example:
                // before: {}
                // after: {creator {name}}
                newSelections = [
                  ...selections,
                  {
                    kind: Kind.FIELD,
                    name: {kind: Kind.NAME, value: field.name},
                    selectionSet: newSelectionSet,
                  },
                ];
              }
              onChangeSelections(newSelections);
            }}
          />
        ) : (
          <FieldRow
            {...sharedProps}
            checkbox={renderCheckbox(isSelected)}
            expandIcon={null}
            onClick={() => {
              onChangeSelections(
                isSelected
                  ? // If already selected, remove from selections
                    // For example:
                    // before: {id}
                    // after: {}
                    selections.filter((selection) => selection.name.value !== field.name)
                  : // Otherwise, add to selections
                    // For example:
                    // before: {}
                    // after: {id}
                    [...selections, {kind: Kind.FIELD, name: {kind: Kind.NAME, value: field.name}}]
              );
            }}
          />
        );
      })}
    </>
  );
}

interface AbstractFieldPossibleTypeProps {
  indentationLevel: number;
  logicalDepth: number;
  possibleType: GraphQLObjectType;
  possibleTypeStateId: string;
  sharedFields: GraphQLFieldMap<unknown, unknown>;
  selections: ReadonlyArray<FieldNode>;
  onChangeSelections: (newSelections: ReadonlyArray<FieldNode>) => void;
}

/**
 * Render a folder for a possible type of an abstract type.
 */
function AbstractFieldPossibleType({
  possibleTypeStateId,
  possibleType,
  sharedFields,
  indentationLevel,
  logicalDepth,
  selections,
  onChangeSelections,
}: AbstractFieldPossibleTypeProps): JSX.Element {
  const {
    logicalDepthLimit,
    getIsTypeDisabled,
    renderExpandIcon,
    renderTypeName,
    getIsExpanded,
    toggleIsExpanded,
    getSortedFilteredFields,
  } = useGraphQLFieldSelectorContext();

  const isExpanded = getIsExpanded(possibleTypeStateId, indentationLevel);

  // Determine which fields are unique to this possible type by filtering out fields that exist on
  // `sharedFields`.
  const uniqueFields = getSortedFilteredFields(
    Object.values(possibleType.getFields()).filter((field) => sharedFields[field.name] == null)
  );

  if (uniqueFields.length === 0) {
    return <></>;
  }

  const numSelected = countSelectedFields(selections);

  const isTypeDisabled = getIsTypeDisabled(getNamedType(possibleType));

  return (
    <>
      <Row
        checkbox={null}
        expandIcon={renderExpandIcon(isExpanded, {
          atMaxDepth: logicalDepth >= logicalDepthLimit,
          isTypeDisabled,
        })}
        indentationLevel={indentationLevel}
        onClick={() => {
          if (logicalDepth < logicalDepthLimit && !isTypeDisabled) {
            toggleIsExpanded(possibleTypeStateId, indentationLevel);
          }
        }}
        text={renderTypeName(getNamedType(possibleType))}
        description={getNumSelectedDescription(isExpanded, numSelected)}
        // TODO(BNCH-54203): Figure out what type icons to use; likely need to thread a prop through for
        // rendering icons based on named type and potentially want to switch `renderFieldTypeIcon` to be
        // similar. Or just use `folder`.
        typeIcon={null}
      />
      {isExpanded && (
        <ObjectFields
          parentStateId={possibleTypeStateId}
          fields={uniqueFields}
          indentationLevel={indentationLevel + 1}
          logicalDepth={logicalDepth + 1}
          selections={selections}
          onChangeSelections={onChangeSelections}
        />
      )}
    </>
  );
}

/** @package */
export interface CompositeFieldProps {
  fieldStateId: string;
  field: GraphQLField;
  indentationLevel: number;
  logicalDepth: number;
  selectionSet: SelectionSetNode;
  onChangeSelectionSet: (newSelectionSet: SelectionSetNode) => void;
}

/**
 * Render a row and subfield/subtype rows for the input composite field.
 *
 * Composite fields can be of three types: Object, Interface, or Union.
 *
 * - Object type: Render a `FieldRow` for the field, then, indented one level, one `FieldRow` or
 *   `CompositeField` for each of fields' subfields.
 * ```
 *   ObjectFieldRow
 *     LeafSubfield1
 *     LeafSubfield2
 *     CompositeSubfield3
 *       SubSubfield1
 *       ...
 *     ...
 * ```
 * - Interface type: Render a `FieldRow` for the field, then, indented one level, one `FieldRow` or
 *   `CompositeField` for each shared field on the interface type, then (at the same indentation level)
 *   one `Row` for each of the interface's implementing types.
 *   Each implementing type must be an Object type, so is rendered like `ObjectFieldRow` in the docstring
 *   above.
 *
 *   TODO(BNCH-54747): Future `graphql` versions may allow interface implementors to be unions and interfaces.
 *   See https://github.com/graphql/graphql-spec/pull/373#issuecomment-375489730
 * ```
 *   InterfaceFieldRow
 *     Subfield1
 *     Subfield2
 *     PossibleType1
 *       Subfield3
 *     PossibleType2
 *       Subfield4
 *       Subfield5
 * ```
 *
 * - Union type: Render a `FieldRow` for the field, then, indented one level, one `Row` for each of the
 *   union's members. Each member must be an Object type, so it is rendered like `ObjectFieldRow` in the
 *   docstring above.
 *
 *   TODO(BNCH-54747): Future `graphql` versions may allow union members to be unions and interfaces.
 *   See https://github.com/graphql/graphql-spec/pull/950
 *
 * ```
 *   UnionFieldRow
 *     MemberType1
 *       Subfield1
 *     MemberType2
 *       Subfield2
 *       Subfield3
 * ```
 *
 * @package
 */
export default function CompositeField({
  field,
  fieldStateId,
  indentationLevel,
  logicalDepth,
  selectionSet,
  onChangeSelectionSet,
}: CompositeFieldProps): JSX.Element {
  const {
    graphQLSchema,
    logicalDepthLimit,
    getIsTypeDisabled,
    getSortedFilteredFields,
    renderExpandIcon,
    getIsExpanded,
    toggleIsExpanded,
    noFieldsDisplayText,
  } = useGraphQLFieldSelectorContext();

  const isExpanded = getIsExpanded(fieldStateId, indentationLevel);
  const numSelected = countSelectedFields(selectionSet.selections);

  const isTypeDisabled = getIsTypeDisabled(getNamedType(field.type));

  const fieldRow = (
    <FieldRow
      field={field}
      checkbox={null}
      expandIcon={renderExpandIcon(isExpanded, {
        atMaxDepth: logicalDepth >= logicalDepthLimit,
        isTypeDisabled,
      })}
      indentationLevel={indentationLevel}
      description={getNumSelectedDescription(isExpanded, numSelected)}
      onClick={() => {
        if (logicalDepth < logicalDepthLimit && !isTypeDisabled) {
          toggleIsExpanded(fieldStateId, indentationLevel);
        }
      }}
    />
  );

  if (!isExpanded) {
    return fieldRow;
  }

  const compositeType = assertCompositeType(getNamedType(field.type));

  // Fields guaranteed to be queryable without a fragment
  const sharedFields =
    isObjectType(compositeType) || isInterfaceType(compositeType) ? compositeType.getFields() : {};

  const sortedSubfields = getSortedFilteredFields(Object.values(sharedFields));

  const possibleTypes = getSortedTypes(
    isAbstractType(compositeType) ? [...graphQLSchema.getPossibleTypes(compositeType)] : []
  );

  const [fieldSelections, fragmentSelections] = partition<FieldNode, InlineFragmentNode>(
    [...selectionSet.selections].map((selection): FieldNode | InlineFragmentNode => {
      assert(
        selection.kind === Kind.FIELD || selection.kind === Kind.INLINE_FRAGMENT,
        'Got unexpected selection kind',
        {selection}
      );
      return selection;
    }),
    (selection) => selection.kind === Kind.FIELD
  );

  if (possibleTypes.length === 0 && sortedSubfields.length === 0) {
    return (
      <>
        {fieldRow}
        <Row
          checkbox={null}
          expandIcon={null}
          text=""
          description={noFieldsDisplayText}
          indentationLevel={indentationLevel + 1}
          onClick={noop}
          typeIcon={null}
        />
      </>
    );
  }

  return (
    <>
      {fieldRow}
      <ObjectFields
        parentStateId={fieldStateId}
        indentationLevel={indentationLevel + 1}
        logicalDepth={logicalDepth + 1}
        fields={sortedSubfields}
        selections={fieldSelections}
        onChangeSelections={(newFieldSelections) => {
          onChangeSelectionSet({
            kind: Kind.SELECTION_SET,
            // TODO(BNCH-54203): Sort these selections to match render order.
            selections: [...newFieldSelections, ...fragmentSelections],
          });
        }}
      />
      {possibleTypes.map((possibleType) => {
        const possibleTypeSelectionIndex = fragmentSelections.findIndex(
          (selection) => selection.typeCondition?.name.value === possibleType.name
        );
        const possibleTypeSelections =
          possibleTypeSelectionIndex >= 0 ? fragmentSelections[possibleTypeSelectionIndex] : null;
        assert(
          possibleTypeSelections == null || possibleTypeSelections.kind === 'InlineFragment',
          'Got non-InlineFragment selection of an abstract type',
          {possibleTypeSelections}
        );

        return (
          <AbstractFieldPossibleType
            possibleTypeStateId={`${fieldStateId}-${possibleType.name}`}
            indentationLevel={indentationLevel + 1}
            key={possibleType.name}
            logicalDepth={
              // We do not increment here as this is a continuation of the same logical depth
              logicalDepth
            }
            sharedFields={sharedFields}
            possibleType={possibleType}
            selections={
              possibleTypeSelections?.selectionSet.selections.map((selection) => {
                // This might happen if you have a query that looks like:
                // ... on XInterface {
                //   ... on SubXInterface {
                //     id
                //   }
                // }
                // This component does not currently support building or rendering such queries.
                assert(selection.kind === 'Field', "Got non-Field selection for possibleType's selections", {
                  selection,
                });
                return selection;
              }) ?? []
            }
            onChangeSelections={(newSelectionsForPossibleType) => {
              let newFragmentSelections: ReadonlyArray<InlineFragmentNode>;
              if (newSelectionsForPossibleType.length === 0 && possibleTypeSelectionIndex >= 0) {
                // If no selections, remove this `possibleType` from `selectionSet` entirely.
                // For example:
                // Before: {... on X {y}}
                // After: {}
                newFragmentSelections = produce(fragmentSelections, (draftSelections) => {
                  draftSelections.splice(possibleTypeSelectionIndex, 1);
                });
              } else if (possibleTypeSelections == null) {
                // If `possibleType` doesn't already have selections in `selectionSet`, add it to
                // `selectionSet` with the new selections.
                // For example:
                // Before: {}
                // After: {... on X {y}}
                newFragmentSelections = [
                  ...fragmentSelections,
                  {
                    kind: Kind.INLINE_FRAGMENT,
                    typeCondition: {
                      kind: Kind.NAMED_TYPE,
                      name: {kind: Kind.NAME, value: possibleType.name},
                    },
                    selectionSet: {kind: Kind.SELECTION_SET, selections: newSelectionsForPossibleType},
                  },
                ];
              } else {
                // Otherwise, `possibleType` already has some selections in `selectionSet`, so update
                // its selections to be the new ones.
                // For example:
                // Before: {... on X {y}}
                // After: {... on X {y z}}
                newFragmentSelections = produce(fragmentSelections, (draftSelections) => {
                  const fragment = draftSelections[possibleTypeSelectionIndex] as InlineFragmentNode;
                  fragment.selectionSet.selections = newSelectionsForPossibleType;
                });
              }

              onChangeSelectionSet({
                kind: Kind.SELECTION_SET,
                // TODO(BNCH-54203): Sort these selections to match render order.
                selections: [...fieldSelections, ...newFragmentSelections],
              });
            }}
          />
        );
      })}
    </>
  );
}
